import { BACKEND_URL } from "../constants";
import {
  ClientInfo,
  ClientToServerSyncMessage,
  ClientToServerSyncMessageType,
  ExpositionState,
  ServerToClientSyncMessage,
  ServerToClientSyncMessageType,
  ChatState,
  KeyState,
  TimeSync,
  ConfigData
} from "../../../shared/interfaces";

import { Socket } from "socket.io";
import { encodeJSON, decodeJSON } from "../../../shared/utils";
import SynchronizedExpositionState from './SynchronizedExpositionState';

const MAX_CHAT_MESSAGES = 18;
const CHAT_AUTO_DELETE_TIME = 15000;

class ExpositionNetworking {

  chatState: ChatState
  socket: Socket
  timeSync: TimeSync

  chatRefreshCallback: (chatState: ChatState) => void
  connectCallbacks: Array<() => void> = [];
  disconnectCallbacks: Array<() => void> = [];
  configChangeCallbacks: Array<(config: ConfigData) => void> = [];

  constructor() {
    this.chatRefreshCallback = null;

    this.chatState = {
      messages: []
    };

    this.timeSync = {
      serverTime: -1,
      clientTime: -1
    }
  }

  initializeNetworking(expositionId: string) {
    const io = require('socket.io-client')(BACKEND_URL);
    this.socket = io.connect();

    this.socket.on('connect', () => {
      console.log('connected with backend via websocket', this.socket.id);

      // join exposition (tell the server who i am)
      this.joinExposition(expositionId)

      this.socket.on('server-to-client-sync', message => this.syncServerToClient(decodeJSON(message), expositionId));

      this.socket.on('disconnect', () => {
        console.log('Lost connection to server...');
        this.disconnectCallbacks.forEach(callback => callback());
      });

      this.connectCallbacks.forEach(callback => callback());
    });
  }

  onChatRefresh(callback: (message: ChatState) => void) {
    this.chatRefreshCallback = callback;
  }

  onConnect(callback: () => void) {
    this.connectCallbacks.push(callback);
  }

  onDisconnect(callback: () => void) {
    this.disconnectCallbacks.push(callback);
  }

  onConfigChange(callback: (config: ConfigData) => void) {
    this.configChangeCallbacks.push(callback);
  }

  emitMessage(message :any) {
    this.socket.emit('client-to-server-sync', encodeJSON(message));
  }

  emitChatMessage(message :string) {
    const sendMessage: ClientToServerSyncMessage = {
      type: ClientToServerSyncMessageType.SEND_MESSAGE,
      expositionId: sessionStorage.getItem('expositionId'),
      participantId: sessionStorage.getItem('myParticipantId'),
      data: {
        name: sessionStorage.getItem('participantName'),
        text: message
      }
    };
    this.emitMessage(sendMessage);
  }

  joinExposition(expositionId: string) {
    let colorConfig = {
      hairColor: '#9e4915',
      skinColor: '#ffd9b5',
      shirtColor: '#42648f'
    }

    const configInStorage = localStorage.getItem('colorConfig');

    if (configInStorage) {
      colorConfig = JSON.parse(configInStorage);
    }

    const clientInfo: ClientInfo = {
      name: sessionStorage.getItem('participantName') || 'Unknown Participant',
      colors: colorConfig
    }
    const clientInfoMessage: ClientToServerSyncMessage = {
      expositionId,
      type: ClientToServerSyncMessageType.CLIENT_INFO,
      participantId: sessionStorage.getItem('myParticipantId'),
      data: clientInfo
    }
    this.emitMessage(clientInfoMessage);
  }

  handleKeyStateUpdate(expositionId: string, fullKeyState: KeyState) {
    let myParticipantId = sessionStorage.getItem('myParticipantId');

    if (myParticipantId) {
      const message: ClientToServerSyncMessage = {
        expositionId,
        type: ClientToServerSyncMessageType.KEY_STATE_UPDATE,
        participantId: myParticipantId,
        data: fullKeyState
      };

      // console.log('send message to backend', message);
      this.emitMessage(message);
    }
  }

  updateTimeSync(serverTime: number) {
    this.timeSync.serverTime = serverTime;
    this.timeSync.clientTime = new Date().getTime();
    SynchronizedExpositionState.updateTimeSync(this.timeSync);
    //const diff = this.timeSync.clientTime - this.timeSync.serverTime;
    //console.log('Updated TimeSync', this.timeSync, diff);
  }

  syncServerToClient(message: ServerToClientSyncMessage, expositionId: string) {
    switch (message.type) {
      case ServerToClientSyncMessageType.EXPOSITION_STATE_UPDATE:
        const serverExpositionState: ExpositionState = message.data as ExpositionState;

        if(this.timeSync.serverTime < 0) {
          this.updateTimeSync(serverExpositionState.serverTime);
        }

        if (serverExpositionState.requirePing) {
          const serverTime = serverExpositionState.serverTime;
          this.updateTimeSync(serverTime);
          const message: ClientToServerSyncMessage = {
            expositionId,
            type: ClientToServerSyncMessageType.PING,
            participantId: sessionStorage.getItem('myParticipantId'),
            data: {
              serverTime
            }
          };
          this.emitMessage(message);
        }

        SynchronizedExpositionState.insertState(serverExpositionState)
        break;

      case ServerToClientSyncMessageType.EXPOSITION_JOINED:
        console.log("received ServerToClientSyncMessageType.EXPOSITION_JOINED message", message);
        sessionStorage.setItem('myParticipantId', message.data.participantId);
        break;

      case ServerToClientSyncMessageType.NEW_MESSAGE:
        this.chatState.messages.push({
          participantId: message.data.participantId,
          name: message.data.name,
          message: message.data.text
        });

        window.setTimeout(()=> {
          this.chatState.messages.shift();
          if(this.chatRefreshCallback) {
            this.chatRefreshCallback(this.chatState);
          }
        }, CHAT_AUTO_DELETE_TIME);

        if(this.chatState.messages.length > MAX_CHAT_MESSAGES) {
          this.chatState.messages.shift();
        }

        if(this.chatRefreshCallback) {
          this.chatRefreshCallback(this.chatState);
        }
        break;

      case ServerToClientSyncMessageType.CONFIG_UPATE:
        const data = message.data as ConfigData
        SynchronizedExpositionState.setEnabled(data.stateInterpolationEnabled);
        SynchronizedExpositionState.setTickRate(data.tickRateHz);
        this.configChangeCallbacks.forEach(callback => callback(data));
        console.log('Configuration has changed', data);
        break;

      default:
        throw "Received ServerToClientSyncMessage with invalid type";
    }
  }

  async fetchMetaData(expositionId: String) {
    const expositionInfoResponse = await fetch(`/api/expositions/${ expositionId }`);
    return await expositionInfoResponse.json();
  }
}


export default new ExpositionNetworking();
